/**
 * Which routes the help agent ("Akeera") may appear on.
 *
 * Its own module because two mounts need it now: HelpAgentRoot (the panel
 * and tour, mounted once in RootLayout) and HelpAgentLauncherSlot (the
 * launcher, rendered by each page header). Importing a page header's worth
 * of component tree just to read a path list would be the wrong direction.
 * HelpAgentRoot re-exports both predicates, so existing callers are
 * unchanged.
 */

/**
 * ALLOWLIST: every destination the PERSISTENT NAV can reach — the left rail
 * (components/nav-config.ts) and the Data sub-sidebar (components/DataSidebar)
 * — plus the two the account menu reaches. That rule is the point: now that
 * the launcher sits in the top header rather than floating, it is chrome, and
 * chrome that is present on four sidebar items and missing on the fifth reads
 * as a bug. `navCoverage.test.ts` fails if a nav item is ever added without a
 * route here.
 *
 * Deep pages still default OUT by not being listed: viewers, editors,
 * builders, wizards, the board canvas, embeds and auth callbacks. Exact-match
 * on the pathname, so sub-routes (/workflow/:id, /data-catalog/match-entity,
 * /human-expert-knowledge/:id) do not inherit.
 *
 * `/data/graph` (the sidebar's Knowledge Graph) IS listed even though it hosts
 * its own BoardChatPanelV2. The old objection was collision — two controls
 * floating in the same corner. In the header there is no collision, and it is
 * a sidebar destination, so leaving it out is exactly the inconsistency this
 * list now exists to prevent.
 */
export const HELP_AGENT_MAIN_ROUTES = [
  // Left rail
  '/intelligent-board',
  '/data-catalog',
  '/digital-coworker',
  '/workflow',
  // Data sub-sidebar
  '/connectors',
  '/human-expert-knowledge',
  '/tag-mapping',
  '/data/graph',
  // Account menu + jobs
  '/settings',
  '/jobs',
] as const

/**
 * `/account-details` is deliberately NOT above, though the floating launcher
 * used to appear there. It renders in OnboardingLayout, which has no app
 * header at all — there is nowhere to put a header control — and no rail,
 * sidebar or account-menu item points at it. Giving an onboarding screen a
 * 60px app header just to host the avatar would be the tail wagging the dog.
 */

const normalizePath = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

export function isHelpAgentExcludedRoute(pathname: string): boolean {
  return !(HELP_AGENT_MAIN_ROUTES as readonly string[]).includes(normalizePath(pathname))
}

/**
 * Surfaces where the widget must NEVER render, even mid-conversation:
 * login, externally-embedded views, auth callbacks, the standalone
 * meeting page. (Ordinary non-main routes only hide the LAUNCHER — an
 * already-open panel or running tour survives navigation there, so an
 * agent `navigate` directive doesn't unmount the answer mid-stream.)
 */
const HARD_EXCLUDED_PREFIXES = ['/embed/', '/auth/callback/', '/__test__/'] as const
// Exact-match excludes (no children): login and the standalone meeting page.
// Exact (not prefix) so a future '/meetings' or '/meeting-recap' isn't caught.
const HARD_EXCLUDED_EXACT = ['/', '/meeting'] as const

export function isHelpAgentHardExcluded(pathname: string): boolean {
  const normalized = normalizePath(pathname)
  if ((HARD_EXCLUDED_EXACT as readonly string[]).includes(normalized)) return true
  return HARD_EXCLUDED_PREFIXES.some((prefix) => normalized.startsWith(prefix))
}
